import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  CardMedia,
  Input,
  MenuItem,
  Select,
  FormControl,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MainContainer from "../../layout/MainContainer";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { ENUM_ROUTES } from "../../RoutesEnum";
import { useGetVenueQuery, useLazyGetVenueStorageQuery } from "../../services/venue";
import { UploadMedia } from "../../utils/mediaUpload";
import { showError, showToast } from "../../constants/toasts";
import {
  useAddBannerMutation,
  useLazyGetBannerDetailQuery,
  useUpdateBannerMutation,
} from "../../services/banner";
import { removeFalsyKeys } from "../../utils/nullKey";
import { VenueType } from "../../types/General";

const durationOptions = [1, 3, 5, 7, 10];
const frequencyOptions = [2, 3, 4, 5, 6];

const TAB_TYPES = {
  PENDING: 0,
  APPROVED: 1,
  REJECTED: 2,
};

const initialValues = {
  title: "",
  venues: [] as string[],
  startDate: dayjs().format("YYYY-MM-DD"),
  endDate: dayjs().format("YYYY-MM-DD"),
  frequency: 2,
  duration: 1,
};

const createValidationSchema = (isEdit: boolean) =>
  Yup.object({
    title: Yup.string().required("Title is required"),
    venues: Yup.array().min(1, "Select at least one venue"),
    startDate: Yup.date().required("Start date is required"),
    endDate: Yup.date()
      .required("End date is required")
      .min(Yup.ref("startDate"), "End date can't be before start date"),
    frequency: Yup.number().required("Frequency is required"),
    duration: Yup.number().required("Duration is required"),
  });

const AddBanner = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: venueList } = useGetVenueQuery({
    limit: 10,
    page: 1,
    search: "",
     status: "APPROVED", 
    type: "VENUE",
  });
  
  
  const [addBanner, { isLoading }] = useAddBannerMutation();
  const [updateBanner] = useUpdateBannerMutation();
  const [image, setImage] = useState<string>("");
  const [bannerData, setBannerData] = useState<any>(null);
  const [bannerDetails] = useLazyGetBannerDetailQuery();
  const skipLoadingRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [venueData, setVenueData] = useState<VenueType[]>([]);
    const [count, setCount] = useState(10);
const [getVenueOwner] = useLazyGetVenueStorageQuery();

  const getDataByIdList = async () => {
    try {
      setLoading(true);
      if (!id) return;
      const response = await bannerDetails({ id }).unwrap();
      if (response.statusCode === 200) {
        setBannerData(response.data);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      showError(error?.message || error?.data?.message);
    }
  };

  useEffect(() => {
    if (id) {
      getDataByIdList();
    }
  }, [id]);

  useEffect(() => {
    if (bannerData) {
      const venueIds =
        bannerData.venueStorageId?.map((venue: any) => venue._id) || [];
      formik.setValues({
        title: bannerData.title || "",
        venues: venueIds,
        startDate: bannerData.startDate
          ? dayjs(bannerData.startDate).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        endDate: bannerData.endDate
          ? dayjs(bannerData.endDate).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        frequency: bannerData.addFrequency || 2,
        duration: bannerData.duration || 1,
      });
      setImage(bannerData.image || "");
    }
  }, [bannerData]);

  const formik = useFormik({
    initialValues,
    validationSchema: createValidationSchema(!!id),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!image) {
          showError("Image is required");
          return;
        }

        const payload: any = {
          image,
          title: values.title,
          venueStorageId: values.venues,
          startDate: values.startDate,
          endDate: values.endDate,
          addFrequency: values.frequency,
          duration: values.duration,
        };

        removeFalsyKeys(payload);

        let response;
        if (id) {
          response = await updateBanner({ body: payload, id }).unwrap();
        } else {
          response = await addBanner(payload).unwrap();
        }

        if (response?.statusCode === 200) {
          showToast(
            id ? "Banner updated successfully" : "Banner added successfully"
          );
          navigate("/manage-banners");
        }
      } catch (error: any) {
        console.error("Error details:", error);
        showError(
          error?.message || error?.data?.message || "Failed to save banner"
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data) {
      setImage(imageUrl.data);
    }
  };

  return (
    <MainContainer>
      <div className="main_title">
        <h1>{id ? "Edit" : "Add"} Banner</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
          <span onClick={() => navigate(ENUM_ROUTES.MANAGE_BANNERS)}>
            Banner Management
          </span>
        </p>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            {/* Upload Image */}
            <div className="control_group w_100">
              <label>Upload Banner (1920x1080)</label>
              {image ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia component="img" image={image} alt="Banner" />
                    </figure>
                    <CloseIcon
                      onClick={() => {
                        setImage("");
                      }}
                    />
                  </div>
                </div>
              ) : (
                <label className="upload_image" htmlFor="icon-button-file">
                  <Input
                    id="icon-button-file"
                    type="file"
                    sx={{ display: "none" }}
                    inputProps={{ accept: "image/png,image/jpeg" }}
                    onChange={(event) => {
                      handleImageUpload(event as any);
                      event.target.value = "";
                    }}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img src="/static/images/placeholder.png" alt="icon" />
                      <span className="upload_img_icon">
                        <img src="/static/images/edit2_icon.svg" alt="edit" />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>

            {/* Title */}
            <div className="control_group w_50">
              <label>Title</label>
              <TextField
                fullWidth
                name="title"
                placeholder="Enter Title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
              />
            </div>

            {/* Venue Multi-Select */}
            <div className="control_group w_50">
              <label>Allocate Venues</label>
              <FormControl
                fullWidth
                error={formik.touched.venues && Boolean(formik.errors.venues)}
              >
                <Select
                  multiple
                  name="venues"
                  displayEmpty
                  value={formik.values.venues}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  input={<OutlinedInput label="Venues" />}
                  renderValue={(selected) =>
                    venueList?.data?.data
                      ?.filter((venue) => selected.includes(venue._id))
                      .map((v) => v.name)
                      .join(", ") || "Please select venue"
                  }
                >
                  {venueList?.data?.data?.map((venue) => (
                    <MenuItem key={venue._id} value={venue._id}>
                      <Checkbox
                        checked={formik.values.venues.includes(venue._id)}
                      />
                      <ListItemText primary={venue.name} />
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.venues && formik.errors.venues && (
                  <Box className="error_text">{formik.errors.venues}</Box>
                )}
              </FormControl>
            </div>

            {/* Start and End Date */}
            <div className="control_group w_25">
              <label>Start Date</label>
              <TextField
                type="date"
                name="startDate"
                fullWidth
                value={formik.values.startDate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                InputLabelProps={{ shrink: true }}
                error={
                  formik.touched.startDate && Boolean(formik.errors.startDate)
                }
                helperText={formik.touched.startDate && formik.errors.startDate}
              />
            </div>
            <div className="control_group w_25">
              <label>End Date</label>
              <TextField
                type="date"
                name="endDate"
                fullWidth
                value={formik.values.endDate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                InputLabelProps={{ shrink: true }}
                error={formik.touched.endDate && Boolean(formik.errors.endDate)}
                helperText={formik.touched.endDate && formik.errors.endDate}
              />
            </div>

            {/* Frequency */}
            <div className="control_group w_25">
              <label>Ad Frequency (Every X Hours)</label>
              <FormControl fullWidth>
                <Select
                  name="frequency"
                  value={formik.values.frequency}
                  onChange={formik.handleChange}
                >
                  {frequencyOptions.map((val) => (
                    <MenuItem key={val} value={val}>
                      Every {val} hour(s)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* Duration */}
            <div className="control_group w_25">
              <label>Duration (Days)</label>
              <FormControl fullWidth>
                <Select
                  name="duration"
                  value={formik.values.duration}
                  onChange={formik.handleChange}
                >
                  {durationOptions.map((day) => (
                    <MenuItem key={day} value={day}>
                      {day} Day(s)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>

          {/* Save Button */}
          <div className="form_btn">
            <Button
              className="btn btn_primary"
              type="submit"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddBanner;
